// Real Google reviews (2026-10-14). The six newest written 4–5★ reviews come
// from the Business Profile API: facebook-leads/gbp_api.py (daily cron)
// rewrites src/data/google-reviews.json when they change, so they ship in the
// prerendered HTML. Text in the reviewer's own words (Google's translation
// stripped), first name + initial. Replaced the bot's NPS feed, which was empty.
import { ExternalLink, Star } from 'lucide-react';
import data from '@/data/google-reviews.json';
import rating from '@/data/google-rating.json';
import { GOOGLE_REVIEWS_URL, GOOGLE_WRITE_REVIEW_URL, Stars, ratingText } from '@/components/cinematic/GoogleRating';

const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const monthYear = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return y && m ? `${MONTHS[m - 1]} ${y}` : '';
};

const StaticReviews = () => {
  // The six newest; the fuller ones first, so the row opens on a real story.
  const reviews = [...data.reviews].sort((a, b) => b.text.length - a.text.length);

  return (
    <section className="font-heebo bg-white py-[clamp(56px,7vw,104px)] px-[clamp(20px,5vw,80px)]" dir="rtl">
      <div className="mx-auto max-w-[1100px]">
        <div className="cin-reveal mb-10 text-center">
          <div className="cin-label-ed mb-4">ביקורות בגוגל</div>
          <h2 className="cin-display-sm text-[var(--cin-ink)]">
            מה אומרים <span className="cin-text-gradient-blue">הלקוחות</span>
          </h2>
        </div>

        {reviews.length > 0 && (
          // Phones: a swipeable row (each card ~85% wide, snapping); desktop: a 3-column grid.
          <ul tabIndex={0} aria-label="ביקורות מגוגל — אפשר לגלול הצידה" className="-mx-5 mb-8 flex items-start md:items-stretch snap-x outline-none focus-visible:ring-2 focus-visible:ring-[var(--cin-blue)] snap-mandatory gap-4 overflow-x-auto px-5 pb-2 [scrollbar-width:none] md:mx-0 md:grid md:grid-cols-3 md:gap-5 md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
            {reviews.map((r, i) => (
              <li
                key={`${r.name}-${r.date}`}
                className={`cin-reveal cin-reveal-d${(i % 3) + 1} cin-tilt w-[85%] shrink-0 snap-center md:w-auto`}
              >
                <figure className="flex flex-col rounded-2xl md:h-full border border-[var(--cin-line)] bg-white p-6">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="inline-flex" role="img" aria-label={`${r.stars} כוכבים`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} aria-hidden="true" className={`h-4 w-4 ${n <= r.stars ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                      ))}
                    </span>
                    <span className="text-xs text-[var(--cin-muted)]">{monthYear(r.date)}</span>
                  </div>
                  <blockquote className="flex-1 whitespace-pre-line text-[15px] leading-relaxed text-[var(--cin-ink)] [overflow-wrap:anywhere] line-clamp-[8]">
                    “{r.text}”
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--cin-blue)]/10 font-bold text-[var(--cin-blue)]" aria-hidden="true">
                      {r.name[0]}
                    </span>
                    <span className="text-sm font-bold text-[var(--cin-ink)]">{r.name}</span>
                    <span className="mr-auto text-xs text-[var(--cin-muted)]">ביקורת בגוגל</span>
                  </figcaption>
                </figure>
              </li>
            ))}
          </ul>
        )}

        <div className="cin-reveal cin-reveal-d1 mx-auto flex max-w-2xl flex-col items-center gap-5 rounded-2xl border border-[var(--cin-line)] bg-[var(--cin-paper-soft)] px-6 py-6 sm:flex-row sm:justify-between sm:px-8">
          {ratingText ? (
            <div className="flex items-center gap-4">
              <div className="cin-counter text-5xl font-extrabold leading-none tracking-tight text-[var(--cin-ink)]">
                {rating.average.toFixed(1)}
              </div>
              <div>
                <Stars size="h-5 w-5" />
                <div className="mt-1 text-sm text-[var(--cin-muted)]">{rating.total} ביקורות בגוגל</div>
                <a href={GOOGLE_WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-sm text-[var(--cin-muted)] underline underline-offset-4 hover:text-[var(--cin-ink)]">
                  הייתם אצלנו? לכתיבת ביקורת
                </a>
              </div>
            </div>
          ) : (
            <p className="text-center text-[15px] leading-relaxed text-[var(--cin-ink)] sm:text-right">
              את הביקורות של לקוחות המעבדה אפשר לקרוא בגוגל.
            </p>
          )}
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cin-btn-primary !px-5 !py-2.5 !text-sm shrink-0"
          >
            לכל הביקורות בגוגל
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default StaticReviews;
